import { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import { TimesheetProofingDistrictModel } from "../../api/models/timesheetProofingDistrictModel";
import { getTimesheetProofingDistricts } from "../../api/services/timesheetProofingService";
import { monthsOfYear, therapistPageSizeOptions, TimesheetStatus } from "../../common/constants";
import Layout from "../../components/layout";
import Modal from "../../components/modal";
import { Notification } from "../../components/notifications";
import TimesheetDistrictEntries from "../../components/timesheetDistrictEntries";
import TimesheetProofingDistrictsRequest from "../../api/requests/timesheetProofingDistrictsRequest";
import { useHistory } from "react-router-dom";
import moment from "moment";
import PaginatedResponse from "../../api/responses/paginatedResponse";
import { Pagination } from "../../components/pagination";


// Auxiliary functions & variables
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
type MonthSelectorProps = {
    range: { start: Moment, end: Moment };
    clickHandler: Function;
}

type PageSizeProps = {
    options: number[];
    outerDivClasses?: string;
    changeHandler: Function;
    currentPageSize: number;
}

type CalendarState = {
    month: number;
    year: number;
};

const dateRangeFormat: string = 'M/D/YY';

const checkForUnresolvedErrors = (districts: TimesheetProofingDistrictModel[]): boolean =>
    districts.some(d => d.issueCount > 0 && d.statusId !== TimesheetStatus.Approved);

const GetNotificationErrorList = (districts: TimesheetProofingDistrictModel[], navigateCallback: CallableFunction | undefined = undefined) => {
    const districtsWithErrors = districts.map((d, i) =>
        (d.issueCount)
            ? <li key={`districtErrorList_${i}`} className='mt-2 ml-8'>
                <a className='font-bold text-white underline cursor-pointer hover:text-black'
                    href={`#district_${d.districtId}`}
                    onClick={(e) => {
                        if (navigateCallback !== undefined) {
                            e.preventDefault();
                            navigateCallback(d);
                        }
                    }}
                >
                    {d.districtName}
                </a>
                <span> -- {d.issueCount} issue(s) found</span>
            </li>
            : null);
    return (
        <Fragment>
            <p>Please review the following errors:</p>
            <ul className="list-disc">{districtsWithErrors}</ul>
        </Fragment>
    );
}



const MonthSelector = ({ range, clickHandler }: MonthSelectorProps) => {
    return (
        <div className='month-selector'>
            <p className='text-sm font-bold'>Currently Viewing</p>
            <div>
                <span className='text-3xl font-bold'>{range.start.format(dateRangeFormat)}</span>
                <span className='text-2xl'> to </span>
                <span className='text-3xl font-bold'>{range.end.format(dateRangeFormat)}</span>
            </div>
            <div className='flex justify-end w-60'>
                <p className='text-sm font-bold text-blue-600 underline cursor-pointer hover:text-blue-800'
                    title='Change date range'
                    onClick={() => clickHandler(true)}>Change</p>
            </div>
        </div>
    );
}

const PageSizeSelector = ({ options, outerDivClasses, changeHandler, currentPageSize }: PageSizeProps) => {
    return (
        <div className={outerDivClasses}>
            <p className='text-sm font-bold'>Records Per Page</p>
            <select className='w-20 mt-2' name='pageSizeSelector' id='pageSizeSelector'
                onChange={(e) => changeHandler(e.target.value)}
                value={currentPageSize}
            >
                {options.map((item, idx) => <option key={`pageSize_${item}`}>{item}</option>)}
            </select>
        </div >
    );
}

const getMonthDateRange = (year: number, month: number) => {
    const startDate = moment([year, month - 1]);
    const endDate = moment(startDate).endOf('month');

    return { start: startDate, end: endDate };
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~



// Main component
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
const Proofing = () => {
    // Variables / Constants
    //---------------------------------------------------------------------------------------------
    const now = new Date();
    const history = useHistory<CalendarState>();
    const districtHistory = useHistory();
    //---------------------------------------------------------------------------------------------


    // States
    //---------------------------------------------------------------------------------------------
    const [datePickerMonth, setDatePickerMonth] = useState<number>(now.getMonth() + 1);
    const [datePickerYear, setDatePickerYear] = useState<number>(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(history.location.state?.month || datePickerMonth);
    const [selectedYear, setSelectedYear] = useState<number>(history.location.state?.year || datePickerYear);
    const [totalRecordCount, setTotalRecordCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
    const [hasUnresolvedErrors, setHasUnresolvedErrors] = useState<boolean>(false);
    const [showErrorsOnly, setShowErrorsOnly] = useState<boolean>(false);
    const [timesheetDistricts, setTimesheetDistricts] = useState<TimesheetProofingDistrictModel[]>();
    const [visibleTimesheetDistricts, setVisibleTimesheetDistricts] = useState<TimesheetProofingDistrictModel[]>();
    //---------------------------------------------------------------------------------------------

    // API operations
    //---------------------------------------------------------------------------------------------
    useEffect(() => {
        // This is not the canonical React approach; this should be eventually updated
        setTimeout(() => loadPageAsync.run(), 1);
    }, [pageNumber, pageSize]);

    const convertPageToZeroIndexing = (pageNumber: number): number => {
        return (pageNumber > 0) ? --pageNumber : 0;
    }

    const getTimesheetProofingDistrictsDefer = async () => {
        if (selectedYear && selectedMonth) {
            const request: TimesheetProofingDistrictsRequest = {
                year: selectedYear,
                month: selectedMonth,
                pageNumber: convertPageToZeroIndexing(pageNumber),
                pageSize: pageSize,
            }
            return await getTimesheetProofingDistricts(request);
        }
        return null;
    }

    const navigateToTimesheetSummary = (dist: TimesheetProofingDistrictModel): void => {
        districtHistory.push(`/proofing/summary/${dist.districtId}`, { district: dist });
    }

    const handleTimesheetProofingDistrictsResponse = (response: any) => {
        const paginatedResponse = response as PaginatedResponse<TimesheetProofingDistrictModel>;
        const timesheetDistricts = paginatedResponse?.data?.records ?? [] as TimesheetProofingDistrictModel[];
        const recordCount: number = paginatedResponse?.data?.totalRecordCount ?? 0;
        const hasErrors = checkForUnresolvedErrors(timesheetDistricts);

        // Save calendar state history
        if (history.location?.pathname) {
            history.replace(history.location.pathname, { year: selectedYear, month: selectedMonth } as CalendarState);
        }

        // Set states
        setShowErrorsOnly(false);
        setOpenDatePicker(false);
        setDatePickerYear(selectedYear);
        setDatePickerMonth(selectedMonth);
        setTimesheetDistricts(timesheetDistricts); // Original copy -- do not modify
        setVisibleTimesheetDistricts(timesheetDistricts); // Displayed districts
        setTotalRecordCount(recordCount);
        setHasUnresolvedErrors(hasErrors);
    }

    const loadPageAsync = useAsync({
        deferFn: getTimesheetProofingDistrictsDefer,
        onResolve: handleTimesheetProofingDistrictsResponse,
    });

    const handleShowErrorsOnly = (displayErrors: boolean) => {
        const timesheetDistrictsWithErrors = visibleTimesheetDistricts?.filter(d => d.issueCount > 0);
        const visibleDistricts = (displayErrors) ? timesheetDistrictsWithErrors : timesheetDistricts;

        setShowErrorsOnly(displayErrors);
        setVisibleTimesheetDistricts(visibleDistricts);
    }

    const handlePageSizeChange = (pageSizeSelection: string) => {
        const pageSize = Number.parseInt(pageSizeSelection);
        setPageNumber(1); // Reset page number on page size change
        setPageSize(pageSize);
    }

    const handlePageChange = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }
    //---------------------------------------------------------------------------------------------


    // UI layout
    //---------------------------------------------------------------------------------------------
    return (
        <Layout isLoading={loadPageAsync.isLoading}>
            <Fragment>
                {/* Month picker modal */}
                <Modal
                    open={openDatePicker}
                    saveText='Set Month/Year'
                    onSave={() => {
                        loadPageAsync.run({
                            year: selectedYear,
                            month: selectedMonth
                        });
                    }}
                    onCancel={() => setOpenDatePicker(false)}
                >
                    <div className='flex space-x-3'>
                        <div className='w-full'>
                            <label className='mb-2'>Month:</label>
                            <select
                                className='w-full'
                                defaultValue={datePickerMonth}
                                onChange={(event) => setSelectedMonth(Number.parseInt(event.target.value))}
                            >
                                {monthsOfYear.map((m, i) => (
                                    <option key={`month_${m.value}`} value={m.value}>
                                        {m.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className='w-full'>
                            <label className='mb-2'>Year:</label>
                            <select
                                className='w-full'
                                defaultValue={datePickerYear}
                                onChange={(event) => setSelectedYear(Number.parseInt(event.target.value))}
                            >
                                {Array.apply(0, Array(10)).map(function (x, i) {
                                    const y = now.getFullYear() - i;
                                    return (
                                        <option key={`year_${y}`} value={y}>
                                            {y}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                </Modal>
                {(hasUnresolvedErrors && visibleTimesheetDistricts) &&
                    <Notification isSuccess={false} isUserCloseable={false}>
                        {GetNotificationErrorList(visibleTimesheetDistricts, navigateToTimesheetSummary)}
                    </Notification>
                }
                <div className='mb-5 text-right'>
                    <input type='checkbox' id='errors-only-checkbox' className='mx-2'
                        checked={showErrorsOnly}
                        onChange={(event) => handleShowErrorsOnly(event.target.checked)} />
                    <label htmlFor='errors-only-checkbox'> Errors Only</label>
                </div>
                <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
                    <div className='flex items-center justify-between'>
                        <MonthSelector
                            range={getMonthDateRange(datePickerYear, datePickerMonth)}
                            clickHandler={setOpenDatePicker}
                        />
                        <PageSizeSelector
                            currentPageSize={pageSize}
                            options={therapistPageSizeOptions}
                            changeHandler={handlePageSizeChange}
                            outerDivClasses='text-right'
                        />
                    </div>
                    {totalRecordCount > 0 &&
                        <Pagination
                            key={`page_${pageNumber}`} // This is critical to parent/child communication
                            totalRecordCount={totalRecordCount}
                            recordLimit={pageSize}
                            parentCurrentPage={pageNumber}
                            changeHandler={handlePageChange}
                        />
                    }
                    {visibleTimesheetDistricts &&
                        <TimesheetDistrictEntries districts={visibleTimesheetDistricts} />
                    }
                </div>

            </Fragment>
        </Layout>
    )
    //---------------------------------------------------------------------------------------------
}
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

export default Proofing;